import {
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { OverrideProductModalComponent } from 'src/app/modals/override-product-modal/override-product-modal.component';
import { ProductOverride } from 'src/app/models/product-override';
import { ProductReference } from 'src/app/models/layout/product-reference';
import { ProductShelfPosition } from 'src/app/models/layout/product-shelf-position';
import { ProductOrientation } from 'src/app/models/layout/product-orientation';
import { LayoutService } from 'src/app/services/layout.service';
import { ModalService } from 'src/app/services/modal.service';
import { ProductInspectorService } from 'src/app/services/product-inspector.service';
import { ProductOverrideService } from 'src/app/services/product-override.service';
import { PlanogramLayout } from 'src/app/models/layout/planogram-layout';
import { SystemRole } from 'src/app/models/roles';
import { RolesService } from 'src/app/services/roles.service';
import { ColumnDef } from 'src/app/models/ColumnDef';
import { ColumnsSelectModalComponent } from 'src/app/modals/columns-select-modal/columns-select-modal.component';
import { CapabilityType } from 'src/app/models/capability-type';
import { CapabilitiesService } from 'src/app/services/capabilities.service';
import { getProductStatusLabel } from 'src/app/models/layout/product-status';
import { FormsModule } from '@angular/forms';
import { NgClass, NgIf } from '@angular/common';
import { HasCapabilityDirective } from '../../../directives/has-capability.directive';

interface GroupedProduct extends ProductReference {
  instances: ProductReference[];
  horizontal: number;
  vertical_min: number;
  vertical_max: number;
}

@Component({
    selector: 'planogram-data-breakdown',
    templateUrl: './data-breakdown.component.html',
    styleUrls: ['./data-breakdown.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        FormsModule,
        NgClass,
        NgIf,
        HasCapabilityDirective,
    ],
})
export class DataBreakdownComponent implements OnInit, OnDestroy {
  SystemRole = SystemRole;
  CapabilityType = CapabilityType;
  private subscriptions: Subscription = new Subscription();
  products: ProductReference[] | null = null;
  groupedProducts: GroupedProduct[] | null = null;
  inspectedProduct: number | null = null;
  filterTerm: string = '';
  isFullscreen: boolean = false;
  sortColumn: string | null = null;
  originalOrder: GroupedProduct[] = [];
  sortDirection: 'asc' | 'desc' = 'asc';
  layoutDOS: Map<number, number> = new Map();
  layoutDFacings: Map<number, { min: number; max: number }> = new Map();
  Number = Number;

  columns: ColumnDef[] = [
    { key: 'enabled', label: 'Enabled', visible: true, locked: true },
    { key: 'productId', label: 'Product Id', visible: false, locked: true },
    { key: 'barcode', label: 'Barcode', visible: true },
    { key: 'nationalId', label: 'National Id', visible: false },
    { key: 'brand', label: 'Brand', visible: true },
    { key: 'description', label: 'Description', visible: true },
    { key: 'uos', label: 'Uos', visible: true },
    { key: 'unit', label: 'Unit', visible: true },
    { key: 'facings', label: 'Facings', visible: true },
    { key: 'maxStack', label: 'Max Stack', visible: false },
    { key: 'casePack', label: 'Case Pack', visible: false },
    { key: 'flow', label: 'Flow', visible: true },
    { key: 'shelfPosition', label: 'Shelf Position', visible: true },
    { key: 'orientation', label: 'Orientation', visible: true },
    { key: 'squeeze', label: 'Squeeze', visible: true },
    { key: 'dosfacing', label: 'DOS/Facing', visible: true },
    { key: 'dos', label: 'DOS', visible: true },
    { key: 'cpi1', label: 'CPI1', visible: true },
    { key: 'cpi2', label: 'CPI2', visible: true },
    { key: 'category', label: 'Category', visible: true },
    { key: 'segment', label: 'Segment', visible: false },
    { key: 'family', label: 'Family', visible: false },
    { key: 'base', label: 'Base', visible: false },
    { key: 'status', label: 'Status', visible: false },
    { key: 'ranged', label: 'Ranged', visible: false },
    { key: 'willAdd', label: 'Will Add', visible: false },
    { key: 'willRemove', label: 'Will Remove', visible: false },
    { key: 'volume', label: 'Volume', visible: true },
    { key: 'sales', label: 'Sales', visible: true },
    { key: 'avgPrice', label: 'Avg Price', visible: false },
    { key: 'brandavgrsp', label: 'Brand Avg RSP', visible: false },
    { key: 'brandmaxrsp', label: 'Brand Max RSP', visible: false },
    { key: 'dimensions', label: 'Dimensions', visible: true },
    { key: 'actions', label: '', visible: true, locked: true },
  ];

  constructor(
    private layoutService: LayoutService,
    private offcanvasService: NgbOffcanvas,
    private modalService: ModalService,
    private productOverrideService: ProductOverrideService,
    public productInspectorService: ProductInspectorService,
    private rolesService: RolesService,
    private capabilitiesService: CapabilitiesService
  ) {}

  ngOnInit(): void {
    this.loadColumnSelections();

    this.subscriptions.add(
      this.layoutService.layout$.subscribe((layout) => {
        if (layout) {
          this.products = [];
          for (let i = 0; i < layout.productOrder.length; i++) {
            let product: ProductReference | undefined =
              layout.productReference.get(layout.productOrder[i]);
            if (product) {
              this.products.push(product);
            }
          }
          this.groupProducts(layout);
        }
      })
    )
    this.subscriptions.add(
      this.layoutService.layoutDOS$.subscribe((dos) => {
        this.layoutDOS = dos;
      })
    )
    this.subscriptions.add(
      this.layoutService.layoutDFacings$.subscribe((dFacings) => {
        this.layoutDFacings = dFacings;
      })
    )
    this.subscriptions.add(
      this.productInspectorService.inspectedProduct$.subscribe((productId) => {
        this.inspectedProduct = productId;
        this.sortProductList();
      })
    );
  }

  openColumnSelect() {
    this.modalService
      .openModalWithOptions(
        ColumnsSelectModalComponent,
        { centered: true, size: 'xl' },
        { data: [...this.columns], name: 'Data Breakdown table' }
      )
      .then((updated: ColumnDef[] | null) => {
        if (updated) {
          this.resetSort();
          this.columns = [...updated];
          this.saveColumnSelections();
        }
      });
  }

  isColumnVisible(key: string): boolean {
    return this.columns.find(c => c.key === key)?.visible ?? false;
  }
  
  // Drive the enabled-toggle off the capability system (not the raw role) so it respects the
  // linked/view-only lock and VIEW shares — and correctly includes APPROVERs, who can edit.
  canEdit(): boolean {
    return this.capabilitiesService.hasCapability(CapabilityType.EDIT_PLANOGRAM);
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    const offcanvasElement = document.querySelector('.offcanvas');
    if (offcanvasElement) {
      if (this.isFullscreen) {
        offcanvasElement.classList.add('offcanvas-fullscreen');
      } else {
        offcanvasElement.classList.remove('offcanvas-fullscreen');
      }
    }
  }

  getLayoutDOS(productId: number): number {
    return this.layoutDOS.get(productId) ?? 0;
  }

  // Live depth-facings display (min/max across placements). Falls back to '0'
  // for products not currently placed in the layout.
  getDFacingsDisplay(productId: number): string {
    const v = this.layoutDFacings.get(productId);
    if (!v) return '0';
    return v.min === v.max ? `${v.min}` : `${v.min}-${v.max}`;
  }

  // Live DOS per facing, derived from the live total DOS and horizontal
  // facing count so that DOS === DOS/Facing × H stays consistent on screen.
  getDosPerFacing(product: GroupedProduct): number {
    if (!product.horizontal) return Number.NaN;
    return this.getLayoutDOS(product.productId) / product.horizontal;
  }

  getFilteredProducts(): GroupedProduct[] {
    if (!this.groupedProducts) return [];

    const term = this.filterTerm.toLowerCase();

    if (!term) return this.groupedProducts;

    return this.groupedProducts.filter((product) => {
      // Always include selected product
      if (product.productId === this.inspectedProduct) return true;

      // Filter by multiple fields
      return (
        product.brand.toLowerCase().includes(term) ||
        product.description.toLowerCase().includes(term) ||
        product.barcode.toLowerCase().includes(term) ||
        product.productId.toString().includes(term)
      );
    });
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.subscriptions.unsubscribe();
  }

  toggleProductEnabled(productId: number, target: EventTarget | null) {
    if (!this.canEdit()) return;
    let enabled = (target as HTMLInputElement).checked;
    this.productOverrideService.toggleProductEnabled(productId, enabled);
  }

  overrideProduct(productId: number) {
    this.modalService
      .openModalWithOptions(
        OverrideProductModalComponent,
        {
          centered: true,
        },
        {
          data: productId,
        }
      )
      .then(async (override: ProductOverride | null) => {
        if (override != null) {
          this.productOverrideService.setOverride(productId, override);
        }
      });
  }

  inspectProduct(productId: number) {
    this.productInspectorService.inspect(productId);
    this.offcanvasService.dismiss();
  }

  clearProductOverride(productId: number) {
    this.productOverrideService.clearOverride(productId);
  }

  getOverride(productId: number): ProductOverride | undefined {
    return this.productOverrideService.getOverride(productId);
  }

  getShelfPosition(shelfPosition: number) {
    return ProductShelfPosition[shelfPosition];
  }

  getOrientation(orientation: number) {
    return ProductOrientation[orientation];
  }

  getStatusLabel(statusId: number | null | undefined): string {
    return getProductStatusLabel(statusId);
  }

  isProductEnabled(productId: number) {
    return this.productOverrideService.isProductEnabled(productId);
  }

  close() {
    this.offcanvasService.dismiss();
  }

  private sortProductList() {
    if (!this.groupedProducts) return;

    this.groupedProducts.sort((a, b) => {
      // Selected product always on top
      if(this.inspectedProduct != null)
      {
        if (a.productId === this.inspectedProduct) return -1;
        if (b.productId === this.inspectedProduct) return 1;
      }

      if (!this.sortColumn) return 0;

      let valA: number | string = '';
      let valB: number | string = '';

      switch (this.sortColumn) {
        case 'uos': 
          valA = a.uos; valB = b.uos; break;
        case 'brand': 
          valA = a.brand; valB = b.brand; break;
        case 'brandAvgRSP':
          valA = a.brandAvgRSP; valB = b.brandAvgRSP; break;
        case 'brandMaxRSP':
          valA = a.brandMaxRSP; valB = b.brandMaxRSP; break;
        case 'avgPrice':
          valA = a.salesData.averagePrice; valB = b.salesData.averagePrice; break;
        case 'sales':
          valA = a.salesData.sales; valB = b.salesData.sales; break;
        case 'volume':
          valA = a.salesData.salesQty; valB = b.salesData.salesQty; break;
        case 'cpi1':
          valA = a.cpi1; valB = b.cpi1; break;
        case 'cpi2':
          valA = a.cpi2; valB = b.cpi2; break;
        case 'dos':
          valA = this.getLayoutDOS(a.productId); 
          valB = this.getLayoutDOS(b.productId); 
          break;
        case 'dosPerFacing':
          valA = this.getDosPerFacing(a); valB = this.getDosPerFacing(b); break;
        case 'squeeze':
          valA = a.squeezeW; valB = b.squeezeW; break;
        case 'flow':
          valA = a.categoryFlow; valB = b.categoryFlow; break;
        case 'maxStack':
          valA = a.maxStack ?? 0; valB = b.maxStack ?? 0; break;
        case 'casePack':
          valA = a.casePack ?? 0; valB = b.casePack ?? 0; break;
      }

      if (typeof valA === 'string' && typeof valB === 'string') {
        return this.sortDirection === 'asc' 
          ? valA.localeCompare(valB) 
          : valB.localeCompare(valA);
      }

      return this.sortDirection === 'asc' 
        ? (Number(valA) - Number(valB)) 
        : (Number(valB) - Number(valA));
    });
  }

  setSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.sortProductList();
  }

  resetSort() {
    if (!this.originalOrder) return;

    this.sortColumn = null;
    this.sortDirection = 'asc';
    this.groupedProducts = [...this.originalOrder];
    this.sortProductList(); // ensure selected product stays pinned
  }

  private groupProducts(layout: PlanogramLayout) {
    if (!this.products) return;

    const groupedMap = new Map<number, GroupedProduct>();

    // First pass - create groups and add instances
    this.products.forEach((product) => {
      if (!groupedMap.has(product.productId)) {
        groupedMap.set(product.productId, {
          ...product,
          instances: [],
          horizontal: 0,
          vertical_min: Infinity,
          vertical_max: -Infinity,
        });
      }
      const group = groupedMap.get(product.productId)!;
      group.instances.push(product);
    });

    // Second pass - calculate facings from layout
    layout.drops.forEach((drop) => {
      drop.shelves.forEach((shelf) => {
        shelf.stacks.forEach((stack) => {
          stack.products.forEach((product) => {
            const group = groupedMap.get(product.productId);
            if (group) {
              group.horizontal += 1;
              group.vertical_min = Math.min(group.vertical_min, product.stackHeight);
              group.vertical_max = Math.max(group.vertical_max, product.stackHeight);
            }
          });
        });
      });
    });

    this.groupedProducts = Array.from(groupedMap.values());
    this.originalOrder = [...this.groupedProducts];
    this.sortProductList();
  }

  // ======================================
  // 🔹 Persistence helpers
  // ======================================
  private loadColumnSelections() {
    const saved = localStorage.getItem('dataBreakdownColumns');
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved) as ColumnDef[];
      this.columns = this.columns.map(col => {
        const match = parsed.find(p => p.key === col.key);
        return match ? { ...col, visible: match.visible } : col;
      });
    } catch {
      console.warn('Failed to parse saved column settings');
    }
  }

  private saveColumnSelections() {
    localStorage.setItem('dataBreakdownColumns', JSON.stringify(this.columns));
  }
}
